import { takeLatest, call, put, select, all } from 'redux-saga/effects';
import {
  loadProducts,
  loadProductsSuccess,
  loadProductsError,
  saveProductRequest,
  adjustStockRequest,
  deleteProductRequest,
} from './actions';
import {
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  adjustStock,
} from '@/services/products';
import { listPrintMaterials } from '@/services/services';
import { getActiveShopId } from '@/utils/shopSession';
import { hydrateProducts, hydratePrintMaterials } from '@/containers/posData/actions';
import type { Product, PrintMaterial } from '@/lib/types';
import type { ProductInput } from '@/services/products';

function mapProduct(dto: any): Product {
  return {
    id: String(dto.id),
    name: dto.name,
    category: dto.category,
    productCode: dto.productCode,
    barcode: dto.barcode || '',
    sku: dto.sku || undefined,
    isCategory: Boolean(dto.isCategory),
    parentId: dto.parentId != null ? String(dto.parentId) : null,
    attributes: dto.attributes ?? {},
    sellingPrice: Number(dto.sellingPrice),
    partyPrice: dto.partyPrice != null ? Number(dto.partyPrice) : undefined,
    costPrice: Number(dto.costPrice),
    stockQuantity: Number(dto.stockQuantity),
    lowStockThreshold: Number(dto.lowStockThreshold),
    isCommissioned: dto.isCommissioned === true,
    supplierCost: dto.supplierCost != null ? Number(dto.supplierCost) : undefined,
    defaultCommission: dto.defaultCommission != null ? Number(dto.defaultCommission) : undefined,
  };
}

function mapMaterial(dto: any): PrintMaterial {
  return {
    id: String(dto.id),
    name: dto.name,
    unit: dto.unit,
    costPerUnit: Number(dto.costPerUnit),
    sellingPrice: Number(dto.sellingPrice ?? 0),
    stockQuantity: Number(dto.stockQuantity ?? 0),
    lowStockThreshold: Number(dto.lowStockThreshold ?? 0),
    category: dto.category ?? 'general',
  };
}

function toInput(product: Product): ProductInput {
  return {
    name: product.name,
    category: product.category,
    productCode: product.productCode,
    barcode: product.barcode,
    sku: product.sku,
    isCategory: product.isCategory,
    parentId: product.parentId,
    attributes: product.attributes,
    sellingPrice: product.sellingPrice,
    partyPrice: product.partyPrice,
    costPrice: product.costPrice,
    stockQuantity: product.stockQuantity,
    lowStockThreshold: product.lowStockThreshold,
    isCommissioned: product.isCommissioned,
    supplierCost: product.supplierCost,
    defaultCommission: product.defaultCommission,
  };
}

export function* handleLoadProducts(): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) {
      yield put(loadProductsError('No active shop selected'));
      return;
    }
    const [rawProducts, rawMaterials]: [any[], any[]] = yield all([
      call(listProducts, shopId),
      call(listPrintMaterials, shopId),
    ]);
    const products = rawProducts.map(mapProduct);
    const materials = rawMaterials.map(mapMaterial);
    yield put(hydrateProducts(products));
    yield put(hydratePrintMaterials(materials));
    yield put(loadProductsSuccess(products));
  } catch (e: any) {
    yield put(loadProductsError(e?.message || 'Failed to load products'));
  }
}

export function* handleSaveProduct(action: ReturnType<typeof saveProductRequest>): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { product, isNew } = action.payload;
    if (isNew) {
      yield call(createProduct, shopId, toInput(product));
    } else {
      yield call(updateProduct, shopId, Number(product.id), toInput(product));
    }
    yield put(loadProducts());
  } catch (e: any) {
    yield put(loadProductsError(e?.message || 'Failed to save product'));
  }
}

export function* handleAdjustStock(action: ReturnType<typeof adjustStockRequest>): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    const { productId, mode, quantity, reason } = action.payload;
    const state: any = yield select((s: any) => s.posData);
    const product = state.products.find((p: Product) => p.id === productId);
    const current = product ? product.stockQuantity : 0;
    const change = mode === 'add' ? quantity : quantity - current;
    yield call(adjustStock, shopId, Number(productId), { change, reason });
    yield put(loadProducts());
  } catch (e: any) {
    yield put(loadProductsError(e?.message || 'Failed to adjust stock'));
  }
}

export function* handleDeleteProduct(
  action: ReturnType<typeof deleteProductRequest>
): Generator<any, void, any> {
  try {
    const shopId = getActiveShopId();
    if (!shopId) return;
    yield call(deleteProduct, shopId, Number(action.payload.id));
    yield put(loadProducts());
  } catch (e: any) {
    yield put(loadProductsError(e?.message || 'Failed to delete product'));
  }
}

export default function* saga() {
  yield takeLatest(loadProducts.type, handleLoadProducts);
  yield takeLatest(saveProductRequest.type, handleSaveProduct);
  yield takeLatest(adjustStockRequest.type, handleAdjustStock);
  yield takeLatest(deleteProductRequest.type, handleDeleteProduct);
}
